import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, Link, Paper, Typography,
} from '@mui/material';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { FixedAssetService } from '../../services/fixedAsset.service';
import { AccountService } from '../../services/account.service';
import { SegmentService } from '../../services/segment.service';
import { Account, Segment } from '../../interfaces/accounting.interface';
import { FixedAsset, FIXED_ASSET_STATUS_COLORS } from '../../interfaces/fixedAsset/fixedAsset.interface';
import { humanizeEnum } from '../../utils/format';
import { formatDate } from '../../utils/dateUtils';
import DisposeAssetDialog from './DisposeAssetDialog';
import AssetValueActionDialog, { ValueAction } from './AssetValueActionDialog'; // B4-E
import AssetValueHistory from './AssetValueHistory'; // B4-E
import { FixedAssetValueService } from '../../services/fixedAssetValue.service'; // B4-E
import { FixedAssetValueSummary } from '../../interfaces/fixedAsset/fixedAssetValue.interface'; // B4-E
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import AttachmentsPanel from '../common/AttachmentsPanel'; // W3-K
import { helpWhy } from '../../help/why';
// ---- F10 ----
import AssetTransferDialog from './AssetTransferDialog';
import AssetLabelDialog from './AssetLabelDialog'; // Q1
import AssetTransferHistory from './AssetTransferHistory';
import { FixedAssetTransferService } from '../../services/fixedAssetTransfer.service';
import { AssetTransfer } from '../../interfaces/fixedAsset/fixedAssetTransfer.interface';
// ---- end F10 ----

const money = (n: number) =>
  (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ flex: '1 1 30%', minWidth: 200 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2">{children}</Typography>
  </Box>
);

const FixedAssetDetail: React.FC = () => {
  const canPost = usePermission(PERMISSIONS.JOURNAL_ENTRY_POST); // B4-E: value events post a journal
  const canManage = usePermission(PERMISSIONS.FIXED_ASSET_MANAGE); // edit / dispose / delete / reverse = FIXED_ASSET_MANAGE server-side
  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams<{ id: string }>();
  const [asset, setAsset] = useState<FixedAsset | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [loading, setLoading] = useState(true);
  const [disposeOpen, setDisposeOpen] = useState(false);
  const [valueSummary, setValueSummary] = useState<FixedAssetValueSummary | null>(null); // B4-E
  const [valueAction, setValueAction] = useState<ValueAction | null>(null); // B4-E
  // ---- F10 ----
  const canTransfer = usePermission(PERMISSIONS.ASSET_TRANSFER_MANAGE);
  const [transfers, setTransfers] = useState<AssetTransfer[]>([]);
  const [transferOpen, setTransferOpen] = useState(false);
  const [labelOpen, setLabelOpen] = useState(false); // Q1
  // ---- end F10 ----

  const load = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const [a, accountsRes, segmentsRes] = await Promise.all([
        FixedAssetService.getById(id),
        AccountService.getLookup(true).catch(() => []),
        SegmentService.getByInstitution(0, 200).catch(() => ({ content: [] })),
      ]);
      setAsset(a);
      setValueSummary(await FixedAssetValueService.summary(id).catch(() => null)); // B4-E
      setTransfers(await FixedAssetTransferService.history(id).catch(err => {   // F10: reported, not swallowed
        toast.showApiError(err, 'Failed to load transfer history');
        return [] as AssetTransfer[];
      }));
      setAccounts(accountsRes ?? []);
      setSegments(segmentsRes?.content ?? []);
    } catch (e) {
      toast.showApiError(e, 'Failed to load asset');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const acct = (accountId?: string) => {
    if (!accountId) return '—';
    const a = accounts.find(x => x.id === accountId);
    return a ? `${a.code} - ${a.name}` : accountId;
  };
  const seg = (segmentId?: string) => {
    if (!segmentId) return 'Common';
    const s = segments.find(x => x.id === segmentId);
    return s ? `${s.code} - ${s.name}` : segmentId;
  };
  const jeLink = (jeId?: string) =>
    jeId ? <Link component={RouterLink} to={`/accounting/journal-entries/${jeId}`}>View JE</Link> : '—';

  const remove = async () => {
    if (!asset || !window.confirm(`Delete ${asset.assetCode}? This cannot be undone.`)) return;
    try {
      await FixedAssetService.remove(asset.id);
      toast.showSuccess('Asset deleted');
      navigate('/fixed-assets');
    } catch (e) {
      toast.showApiError(e, 'Failed to delete asset');
    }
  };

  const reverse = async () => {
    if (!asset || !window.confirm(`Reverse the disposal of ${asset.assetCode}? The asset becomes active again and a reversing journal entry is posted.`)) return;
    try {
      await FixedAssetService.disposeReverse(asset.id);
      toast.showSuccess('Disposal reversed');
      load();
    } catch (e) {
      toast.showApiError(e, 'Failed to reverse disposal');
    }
  };

  const reverseEvent = async (eventId: string) => {
    if (!asset || !window.confirm('Reverse the latest value event?')) return;
    try {
      await FixedAssetValueService.reverseEvent(asset.id, eventId);
      toast.showSuccess('Value event reversed');
      load();
    } catch (e) {
      toast.showApiError(e, 'Failed to reverse value event');
    }
  };

  // F10: only the latest posted transfer is reversible; a reversal of a segment reclass posts the mirror journal.
  const reverseTransfer = async (t: AssetTransfer) => {
    const msg = t.journalEntryId
      ? `Reverse ${t.transferNo}? This restores the previous placement and posts a reversal of the reclass journal.`
      : `Reverse ${t.transferNo}? This restores the previous placement.`;
    if (!window.confirm(msg)) return;
    try {
      await FixedAssetTransferService.reverse(t.id);
      toast.showSuccess(`Transfer ${t.transferNo} reversed`);
      load();
    } catch (e) {
      toast.showApiError(e, 'Failed to reverse transfer');
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  if (!asset) return <Box sx={{ p: 3 }}><Typography>Asset not found.</Typography></Box>;

  const disposed = asset.status === 'DISPOSED';
  // Mirrors FixedAssetService.delete: a posted acquisition journal blocks delete unless it is an untouched bill registration.
  const untouchedBillRegistration = asset.acquisitionSource === 'SUPPLIER_BILL' && asset.status === 'ACTIVE'
    && (asset.accumulatedDepreciation ?? 0) === 0 && !((asset.accumulatedImpairment ?? 0) > 0)
    && (asset.grossCost ?? asset.acquisitionCost) === asset.acquisitionCost;
  const deleteBlocked = !!asset.acquisitionJournalEntryId && !untouchedBillRegistration;
  const nbv = valueSummary ? valueSummary.carryingAmount : asset.acquisitionCost - asset.accumulatedDepreciation;

  return (
    <Box sx={{ p: 3, maxWidth: 1000 }} data-help-page="fixedAssets.detail.page">
      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5">{asset.assetCode} — {asset.name}</Typography>
          <Chip size="small" label={humanizeEnum(asset.status)} color={FIXED_ASSET_STATUS_COLORS[asset.status]} />
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          <Button data-help="fixedAssets.detail.schedule" variant="outlined" onClick={() => navigate(`/fixed-assets/${asset.id}/schedule`)}>
            View schedule
          </Button>
          <Button data-help="fixedAssets.detail.printLabel" variant="outlined" onClick={() => setLabelOpen(true)}>Print label</Button>{/* Q1 */}
          {canTransfer && !disposed && (
            <Button data-help="fixedAssets.detail.transfer" variant="outlined" onClick={() => setTransferOpen(true)}>Transfer</Button>
          )}{/* F10 */}
          {canManage && !disposed && (
            <>
              <Button data-help="fixedAssets.detail.edit" variant="outlined" onClick={() => navigate(`/fixed-assets/${asset.id}/edit`)}>Edit</Button>
              {canPost && asset.status === 'ACTIVE' && (
                <>
                  <Button data-help="fixedAssets.detail.revalue" variant="outlined" onClick={() => setValueAction('REVALUE')}>Revalue</Button>
                  <Button data-help="fixedAssets.detail.impair" variant="outlined" onClick={() => setValueAction('IMPAIR')}>Impair</Button>
                  {(valueSummary?.impairmentReversalHeadroom ?? 0) > 0 && (
                    <Button data-help="fixedAssets.detail.reverseImpairment" variant="outlined" onClick={() => setValueAction('REVERSE_IMPAIRMENT')}>Reverse impairment</Button>
                  )}
                </>
              )}
              {canPost && (valueSummary?.revaluationReserveBalance ?? 0) > 0 && (
                <Button data-help="fixedAssets.detail.reserveTransfer" variant="outlined" onClick={() => setValueAction('RESERVE_TRANSFER')}>Reserve transfer</Button>
              )}
              <Button data-help="fixedAssets.detail.dispose" variant="outlined" color="warning" onClick={() => setDisposeOpen(true)}>Dispose</Button>
              <Button data-help="fixedAssets.detail.delete" {...helpWhy(deleteBlocked ? { kind: 'custom', message: 'This asset has a posted journal entry and cannot be deleted. Dispose it instead.' } : null)} disabled={deleteBlocked} variant="outlined" color="error" onClick={remove}>Delete</Button>
            </>
          )}
          {canManage && disposed && (
            <Button data-help="fixedAssets.detail.reverseDisposal" variant="contained" onClick={reverse}>Reverse disposal</Button>
          )}
        </Box>
      </Box>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Field label="Category">{asset.category ?? '—'}</Field>
          <Field label="Acquisition date">{formatDate(asset.acquisitionDate)}</Field>
          <Field label="Acquisition source">
            {asset.acquisitionSource === 'REGISTERED_EXISTING'
              ? <Chip size="small" label="Registered (existing)" />
              : asset.acquisitionSource === 'SUPPLIER_BILL' ? 'Supplier bill' : 'New purchase'}
          </Field>
          <Field label="Acquisition cost">{money(asset.acquisitionCost)}</Field>
          <Field label="Salvage value">{money(asset.salvageValue)}</Field>
          <Field label="Useful life (months)">{asset.usefulLifeMonths}</Field>
          <Field label="Depreciation method">{humanizeEnum(asset.depreciationMethod)}</Field>
          <Field label="Depreciation start">{formatDate(asset.depreciationStartDate)}</Field>
          <Field label="Segment">{seg(asset.segmentId)}</Field>
          {/* ---- F10 placement ---- */}
          <Field label="Location">{asset.locationName ?? '—'}</Field>
          <Field label="Department">{asset.departmentName ?? '—'}</Field>
          <Field label="Custodian">{asset.custodianName ?? '—'}</Field>
          {/* ---- end F10 ---- */}
          <Field label="Asset account">{acct(asset.assetAccountId)}</Field>
          <Field label="Accumulated-depreciation account">{acct(asset.accumulatedDepreciationAccountId)}</Field>
          <Field label="Depreciation-expense account">{acct(asset.depreciationExpenseAccountId)}</Field>
          <Field label="Accumulated depreciation">{money(asset.accumulatedDepreciation)}</Field>
          <Field label="Depreciated through">
            {asset.depreciatedThrough ? formatDate(asset.depreciatedThrough) : 'Not started'}
          </Field>
          <Field label="Net book value">{money(nbv)}</Field>
          <Field label="Acquisition JE">
            {asset.acquisitionSource === 'REGISTERED_EXISTING' && !asset.acquisitionJournalEntryId
              ? 'Registered (no JE)' : jeLink(asset.acquisitionJournalEntryId)}
          </Field>
        </Box>
      </Paper>

      {valueSummary && (
        <AssetValueHistory summary={valueSummary} canManage={canManage && canPost && !disposed} onReverse={reverseEvent} />
      )}

      <AssetTransferHistory transfers={transfers} canReverse={canTransfer} canReverseReclass={canManage}
        onReverse={reverseTransfer} />{/* F10 */}

      {disposed && (
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Disposal</Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Field label="Disposal date">{asset.disposalDate ? formatDate(asset.disposalDate) : '—'}</Field>
            <Field label="Proceeds">{money(asset.disposalProceeds ?? 0)}</Field>
            <Field label="Disposal JE">{jeLink(asset.disposalJournalEntryId)}</Field>
          </Box>
        </Paper>
      )}

      {!disposed && (
        <Alert severity="info">
          Need to change cost or useful life? They are locked once depreciation starts. The supported
          workaround is to <b>dispose</b> this asset and <b>re-register</b> it with the corrected values —
          note this may realise a disposal gain/loss.
        </Alert>
      )}

      {valueAction && (
        <AssetValueActionDialog open action={valueAction} assetId={asset.id} institutionId={asset.institutionId}
          onClose={() => setValueAction(null)} onDone={() => { setValueAction(null); load(); }} />
      )}
      {asset && (
        <DisposeAssetDialog
          open={disposeOpen}
          asset={asset}
          onClose={() => setDisposeOpen(false)}
          onDisposed={() => { setDisposeOpen(false); load(); }}
        />
      )}
      {transferOpen && (
        <AssetTransferDialog open asset={asset} canChangeSegment={canManage}
          onClose={() => setTransferOpen(false)} onDone={() => { setTransferOpen(false); load(); }} />
      )}{/* F10 */}
      {labelOpen && <AssetLabelDialog open assetIds={[asset.id]} onClose={() => setLabelOpen(false)} />}{/* Q1 */}
      {id && <AttachmentsPanel entityType="FIXED_ASSET" entityId={id} canManage={canManage} />} {/* W3-K */}
    </Box>
  );
};

export default FixedAssetDetail;
